import React, { useEffect, useState } from 'react';
import { Cell as CellType } from '../types';
import { TETROMINOS } from '../tetrominos';

interface CellProps {
  cell: CellType;
  isNew?: boolean; // Flag for newly spawned piece
}

// Individual cell component, rendered based on tetromino type
const Cell: React.FC<CellProps> = ({ cell: [type, status], isNew = false }) => {
  // Animation state for entry effect
  const [showEntryAnimation, setShowEntryAnimation] = useState(isNew);
  
  useEffect(() => {
    if (isNew) {
      // Remove entry animation after it completes
      const timer = setTimeout(() => setShowEntryAnimation(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isNew]);
  
  // Determine block color
  const baseColor = typeof type === 'string' && TETROMINOS[type]
    ? TETROMINOS[type].color
    : '0, 0, 0';
  const isFilled = !!type;
  const isLocked = status === 'merged';

  // Styling values
  const background = isFilled ? `rgba(${baseColor}, 0.8)` : 'transparent';
  const border = isFilled ? `2px solid rgba(${baseColor}, 1)` : '0';
  const borderBottomColor = isFilled ? `rgba(${baseColor}, 0.1)` : undefined;
  const borderRightColor = isFilled ? `rgba(${baseColor}, 1)` : undefined;

  // Enhanced glow effect
  const boxShadow = isFilled
    ? isLocked
      ? `0 0 6px rgba(${baseColor}, 0.6), inset 0 0 3px rgba(${baseColor}, 0.8)`
      : `0 0 10px rgba(${baseColor}, 0.8), inset 0 0 4px rgba(${baseColor}, 1)`
    : undefined;

  // Animations
  let animation;
  if (isLocked) {
    animation = 'pop 0.2s ease-out forwards';
  } else if (showEntryAnimation && isFilled) {
    animation = 'pieceEntry 0.3s ease-out forwards';
  }

  const style: React.CSSProperties = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    background,
    border,
    borderBottomColor,
    borderRightColor,
    boxShadow,
    transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
    animation,
    position: 'relative',
  };

  // Add inner highlight for premium look
  return (
    <div style={style}>
      {isFilled && (
        <div 
          style={{
            position: 'absolute',
            top: '15%',
            left: '15%',
            width: '30%',
            height: '30%',
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(${baseColor}, 0.5) 0%, transparent 70%)`,
            opacity: 0.8,
          }}
        />
      )}
    </div>
  );
};

export default React.memo(Cell);